import {
    Alert,
    Box,
    Card,
    CardContent,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import KeyValue from './keyValue';
import useTranslate from 'hooks/useTranslate';
import type { MilestoneInterface } from 'types';

interface Props {
    milestones: MilestoneInterface[];
    applicationPaymentStatus?: string | null;
}

const MilestoneTable = ({ milestones }: Props) => {
    const theme = useTheme();
    const { translate } = useTranslate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const calculateTotal = () => {
        if (!milestones || milestones.length === 0) return 0;
        return milestones.reduce((sum, milestone) => sum + milestone.price, 0);
    };

    const getCurrencySymbol = () => {
        if (!milestones || milestones.length === 0) return 'USD';
        return milestones[0].currency || 'USD';
    };

    if (milestones.length === 0) {
        return (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No milestones found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    This application doesn't have any milestones yet.
                </Typography>
            </Paper>
        );
    }

    // Mobile Card View
    if (isMobile) {
        return (
            <Box sx={{ mb: 3 }}>
                <Stack spacing={2}>
                    {milestones.map((milestone) => {
                        return (
                            <Card key={milestone.id} variant="outlined">
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 600 }}
                                                gutterBottom
                                            >
                                                {milestone.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.875rem' }}
                                            >
                                                {milestone.description}
                                            </Typography>
                                        </Box>

                                        <Stack direction="column" spacing={0}>
                                            <KeyValue
                                                label={translate('fee')}
                                                value={`${getCurrencySymbol()} ${milestone.price.toFixed(2)}`}
                                                valueColor="primary.main"
                                                valueFontWeight={600}
                                            />
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        );
                    })}

                    {/* Total Card - Separate from milestones */}
                    <Card
                        variant="outlined"
                        sx={{ bgcolor: 'primary.50', borderColor: 'primary.main' }}
                    >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Stack
                                direction="row"
                                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {translate('total')}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    color="primary.main"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {getCurrencySymbol()} {calculateTotal().toFixed(2)}
                                </Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>

                {/* Important Note */}
                <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>{translate('notes')}:</strong> {translate('promptRetainerPayments')}
                </Alert>
            </Box>
        );
    }

    // Desktop Table View
    return (
        <Paper sx={{ mb: 3 }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('name')}</TableCell>
                            <TableCell colSpan={2}>{translate('description')}</TableCell>
                            <TableCell align="right">
                                {translate('fee')} ({getCurrencySymbol()})
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {milestones.map((milestone) => {
                            return (
                                <TableRow key={milestone.id}>
                                    <TableCell>{milestone.name}</TableCell>
                                    <TableCell colSpan={2}>{milestone.description}</TableCell>
                                    <TableCell align="right">
                                        {milestone.price.toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {/* Total Row */}
                        <TableRow>
                            <TableCell colSpan={3} align="right">
                                <Typography variant="h6">Total:</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="h6">{calculateTotal().toFixed(2)}</Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Important Note */}
            <Box sx={{ px: 2, py: 1 }}>
                <Alert severity="info" sx={{ width: 'fit-content' }}>
                    <strong>{translate('notes')}:</strong> {translate('promptRetainerPayments')}
                </Alert>
            </Box>
        </Paper>
    );
};

export default MilestoneTable;
